import React from "react";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonList,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonButton,
  IonIcon,
  IonAlert,
} from "@ionic/react";
import {
  logOutOutline,
  personOutline,
  callOutline,
  mailOutline,
  idCardOutline,
  calendarNumberOutline,
} from "ionicons/icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ApiService } from "../services/api.service";
import "./Tab3.css";

interface Tab3Props {
  onLogout?: () => void;
}

const Tab3: React.FC<Tab3Props> = ({ onLogout }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [showLogoutAlert, setShowLogoutAlert] = React.useState(false);

  // User information - in a real app, this would come from authentication context/service
  const userInfo = JSON.parse(localStorage.getItem("userData") || "{}");

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language);
  };

  const handleLogout = () => {
    // Clear auth token
    ApiService.clearAuthToken();

    // Clear any stored user data
    localStorage.removeItem("userEmail");
    localStorage.removeItem("rememberMe");

    // Call the logout callback
    if (onLogout) {
      onLogout();
    }

    // Navigate to login page
    history.push("/login");
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t("tabs.settings")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">{t("tabs.settings")}</IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>{t("settings.language")}</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList>
              <IonItem>
                <IonLabel>{t("settings.selectLanguage")}</IonLabel>
                <IonSelect
                  value={i18n.language}
                  placeholder={t("settings.selectLanguage")}
                  onIonChange={(e) => changeLanguage(e.detail.value)}
                >
                  <IonSelectOption value="en">
                    {t("languages.english")}
                  </IonSelectOption>
                  <IonSelectOption value="mr">
                    {t("languages.marathi")}
                  </IonSelectOption>
                </IonSelect>
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        {/* User Information Section */}
        <IonCard className="user-info-card">
          <IonCardHeader>
            <IonCardTitle>
              <IonIcon
                icon={personOutline}
                style={{ marginRight: "8px", verticalAlign: "middle" }}
              />
              {t("settings.userInfo")}
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList>
              <IonItem className="user-info-item">
                <IonIcon icon={personOutline} slot="start" color="primary" />
                <IonLabel>
                  <h3>{t("settings.name")}</h3>
                  <p>
                    {userInfo.firstName} {userInfo.lastName}
                  </p>
                </IonLabel>
              </IonItem>
              <IonItem className="user-info-item">
                <IonIcon icon={callOutline} slot="start" color="primary" />
                <IonLabel>
                  <h3>{t("settings.phone")}</h3>
                  <p>{userInfo.emailOrMobile}</p>
                </IonLabel>
              </IonItem>
              <IonItem className="user-info-item">
                <IonIcon icon={mailOutline} slot="start" color="primary" />
                <IonLabel>
                  <h3>{t("settings.email")}</h3>
                  <p>{userInfo.emailOrMobile}</p>
                </IonLabel>
              </IonItem>
              <IonItem className="user-info-item">
                <IonIcon icon={idCardOutline} slot="start" color="primary" />
                <IonLabel>
                  <h3>{t("settings.userId")}</h3>
                  <p>{userInfo.userId || userInfo.id}</p>
                </IonLabel>
              </IonItem>
              <IonItem className="user-info-item">
                <IonIcon
                  icon={calendarNumberOutline}
                  slot="start"
                  color="primary"
                />
                <IonLabel>
                  <h3>{t("settings.lastLogin")}</h3>
                  <p>{userInfo.lastLogin}</p>
                </IonLabel>
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>{t("settings.appInfo")}</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList>
              <IonItem>
                <IonLabel>
                  <h3>{t("settings.version")}</h3>
                  <p>0.0.1</p>
                </IonLabel>
              </IonItem>
              <IonItem>
                <IonLabel>
                  <h3>{t("settings.platform")}</h3>
                  <p>Ionic React</p>
                </IonLabel>
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>{t("settings.account")}</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonButton
              expand="block"
              fill="outline"
              color="danger"
              onClick={() => setShowLogoutAlert(true)}
            >
              <IonIcon icon={logOutOutline} slot="start" />
              {t("settings.logout")}
            </IonButton>
          </IonCardContent>
        </IonCard>

        <IonAlert
          isOpen={showLogoutAlert}
          onDidDismiss={() => setShowLogoutAlert(false)}
          header={t("settings.confirmLogout")}
          message={t("settings.logoutMessage")}
          buttons={[
            {
              text: t("common.cancel"),
              role: "cancel",
            },
            {
              text: t("settings.logout"),
              role: "confirm",
              handler: handleLogout,
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default Tab3;
